import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import "./EscolheBemGame.css";
import { isTabletWidthOnly as isTablet } from "../shared/device";
import { byTag, PRODUCTS_BASE_PATH } from "../../products/catalog.generated";

type GameStatus = "idle" | "running" | "finished";

interface PointGroupConfig {
  id: string;
  points: number;
  products: string[];
}

interface GameConfig {
  durationSeconds: number;
  targetScore: number;
  productsPerSecond: number;
  finalRushSeconds: number;
  finalRushSpeedMultiplier: number;
  pointGroups: PointGroupConfig[];
  victoryProductStock: Record<string, number>;
}

interface FallingItem {
  id: number;
  imagePath: string;
  points: number;
  xPercent: number;
  y: number;
  speed: number;
  rotation: number;
  // perf: cache the scaled sprite directly on the item so drawCanvasFrame skips the per-draw string concat + Map lookup; nulled on resize
  scaledSprite: HTMLCanvasElement | null;
}

interface EscolheBemGameProps {
  onBackToHome: () => void;
}

const GAME_DURATION_SECONDS = 20;
const TARGET_SCORE = 70;
const SPAWN_INTERVAL_MS = 500;
const DEFAULT_PRODUCTS_PER_SECOND = Number(
  (1000 / SPAWN_INTERVAL_MS).toFixed(1),
);
const MIN_PRODUCTS_PER_SECOND = 0.3;
const MAX_PRODUCTS_PER_SECOND = 6;
const DEFAULT_FINAL_RUSH_SECONDS = 7;
const MIN_FINAL_RUSH_SECONDS = 0;
const DEFAULT_FINAL_RUSH_SPEED_MULTIPLIER = 1.35;
const MIN_FINAL_RUSH_SPEED_MULTIPLIER = 1;
const MAX_FINAL_RUSH_SPEED_MULTIPLIER = 3;
const DEFAULT_ITEM_BASE_SPEED = 290;
const MIN_ITEM_SPEED = 110;
const MAX_ITEM_SPEED = 340;
const POINTS_FEEDBACK_DURATION_MS = 1200;
const MANAGEMENT_TAP_WINDOW_MS = 1500;
const MANAGEMENT_TAP_COUNT = 5;
const CONFIG_STORAGE_KEY = "escolheBemConfig";
const PRIZE_GIVEN_STORAGE_KEY = "escolheBemPrizeGiven";
const MAX_ACTIVE_ITEMS = 24;
const MAX_ACTIVE_ITEMS_MIN = 12;
const MAX_SPAWNS_PER_FRAME = 3;
const REFERENCE_ARENA_AREA = 1024 * 576;
const MAX_DELTA_MS = 40;
const MAX_CANVAS_PIXEL_RATIO = 1;
const ITEM_SIZE_MIN = 74;
const ITEM_SIZE_MAX = 152;
const ITEM_SIZE_RATIO = 0.115;
const ENABLE_ITEM_ROTATION = false;
const ESCOLHE_BEM_PRODUCTS_BASE_PATH = PRODUCTS_BASE_PATH;
const ESCOLHE_BEM_OPTIMIZED_PRODUCTS_BASE_PATH = "/images/products-escolhe-bem";
const POSITIVE_PRODUCT_IMAGES = byTag("healthy");
const NEGATIVE_PRODUCT_IMAGES = byTag("unhealthy");
const ALL_PRODUCT_IMAGES = [
  ...POSITIVE_PRODUCT_IMAGES,
  ...NEGATIVE_PRODUCT_IMAGES,
];
const ALL_VICTORY_PRODUCT_IMAGES = byTag("escolhe-bem-victory");

const ALL_CONFIGURABLE_IMAGE_PATHS = [
  ...ALL_PRODUCT_IMAGES,
  ...ALL_VICTORY_PRODUCT_IMAGES,
];

const DEFAULT_VICTORY_PRODUCT_STOCK = 10;

function getEligibleVictoryProducts(
  stock: Record<string, number>,
): string[] {
  return ALL_VICTORY_PRODUCT_IMAGES.filter((path) => (stock[path] ?? 0) > 0);
}

function buildEmptyStockMap(): Record<string, number> {
  const map: Record<string, number> = {};
  for (const path of ALL_VICTORY_PRODUCT_IMAGES) {
    map[path] = 0;
  }
  return map;
}

function buildDefaultStockMap(): Record<string, number> {
  const map: Record<string, number> = {};
  for (const path of ALL_VICTORY_PRODUCT_IMAGES) {
    map[path] = DEFAULT_VICTORY_PRODUCT_STOCK;
  }
  return map;
}

const DEFAULT_GAME_CONFIG: GameConfig = {
  durationSeconds: GAME_DURATION_SECONDS,
  targetScore: TARGET_SCORE,
  productsPerSecond: DEFAULT_PRODUCTS_PER_SECOND,
  finalRushSeconds: DEFAULT_FINAL_RUSH_SECONDS,
  finalRushSpeedMultiplier: DEFAULT_FINAL_RUSH_SPEED_MULTIPLIER,
  victoryProductStock: buildDefaultStockMap(),
  pointGroups: [
    {
      id: "group-positive",
      points: 10,
      products: [...POSITIVE_PRODUCT_IMAGES],
    },
    {
      id: "group-negative",
      points: -10,
      products: [...NEGATIVE_PRODUCT_IMAGES],
    },
  ],
};

function migrateEscolheBemProductPath(path: string) {
  if (path.startsWith(`${ESCOLHE_BEM_OPTIMIZED_PRODUCTS_BASE_PATH}/`)) {
    const optimizedFileName = path.split("/").pop();
    if (!optimizedFileName) return path;

    const originalFileName = optimizedFileName.replace(/\.webp$/i, ".png");
    return `${ESCOLHE_BEM_PRODUCTS_BASE_PATH}/${originalFileName}`;
  }

  const fileName = path.split("/").pop();
  if (!fileName) return path;

  const migratedPath = `${ESCOLHE_BEM_PRODUCTS_BASE_PATH}/${fileName}`;
  if (ALL_CONFIGURABLE_IMAGE_PATHS.includes(migratedPath)) {
    return migratedPath;
  }

  return path;
}

function formatSignedPoints(points: number) {
  return points > 0 ? `+${points}` : `${points}`;
}

function getRandomFrom<T>(items: T[]) {
  return items[Math.floor(Math.random() * items.length)];
}

function clampValue(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

function getItemSpeed() {
  return clampValue(DEFAULT_ITEM_BASE_SPEED, MIN_ITEM_SPEED, MAX_ITEM_SPEED);
}

function productsPerSecondToInterval(productsPerSecond: number) {
  return (
    1000 /
    clampValue(
      productsPerSecond,
      MIN_PRODUCTS_PER_SECOND,
      MAX_PRODUCTS_PER_SECOND,
    )
  );
}

function getDynamicMaxActiveItems(arenaWidth: number, arenaHeight: number) {
  const areaScale = (arenaWidth * arenaHeight) / REFERENCE_ARENA_AREA;
  const scaledLimit = Math.round(MAX_ACTIVE_ITEMS * areaScale);

  return clampValue(scaledLimit, MAX_ACTIVE_ITEMS_MIN, MAX_ACTIVE_ITEMS);
}

function formatProductLabel(imagePath: string) {
  return (
    imagePath
      .split("/")
      .pop()
      ?.replace(/\.(png|jpe?g|webp)$/i, "")
      .replace(/-/g, " ") ?? "produto"
  );
}

function toOptimizedEscolheBemProductPath(imagePath: string) {
  const normalizedPath = migrateEscolheBemProductPath(imagePath);

  if (!normalizedPath.startsWith(`${ESCOLHE_BEM_PRODUCTS_BASE_PATH}/`)) {
    return normalizedPath;
  }

  const fileName = normalizedPath.split("/").pop();
  if (!fileName) return normalizedPath;

  const webpFileName = fileName.replace(/\.[^.]+$/, ".webp");
  return `${ESCOLHE_BEM_OPTIMIZED_PRODUCTS_BASE_PATH}/${webpFileName}`;
}

function toOriginalEscolheBemProductPath(imagePath: string) {
  return migrateEscolheBemProductPath(imagePath);
}

function resolvePublicAssetPath(path: string) {
  if (/^(https?:)?\/\//i.test(path)) return path;

  const publicUrl = (process.env.PUBLIC_URL ?? "").replace(/\/$/, "");
  if (!publicUrl) return path;

  if (path.startsWith("/")) {
    return `${publicUrl}${path}`;
  }

  return `${publicUrl}/${path}`;
}

function handleProductImageLoadError(
  event: React.SyntheticEvent<HTMLImageElement>,
  fallbackPath: string,
) {
  const imageElement = event.currentTarget;
  if (imageElement.dataset.fallbackApplied === "true") return;

  imageElement.dataset.fallbackApplied = "true";
  imageElement.src = resolvePublicAssetPath(fallbackPath);
}

function buildDefaultConfig(): GameConfig {
  return JSON.parse(JSON.stringify(DEFAULT_GAME_CONFIG)) as GameConfig;
}

// Read and sanitize the persisted config synchronously so initial state (config
// and the admin stock drafts) reflects what's actually stored, with no flash of
// defaults and no post-mount load race.
function loadInitialConfig(): GameConfig {
  if (typeof window === "undefined") return buildDefaultConfig();

  try {
    const stored = window.localStorage.getItem(CONFIG_STORAGE_KEY);
    if (!stored) return buildDefaultConfig();
    return sanitizeConfig(JSON.parse(stored));
  } catch {
    console.warn("Configuração inválida no localStorage para Escolhe Bem");
    return buildDefaultConfig();
  }
}

function loadGivenCounts(): Record<string, number> {
  const result = buildEmptyStockMap();
  if (typeof window === "undefined") return result;

  try {
    const raw = window.localStorage.getItem(PRIZE_GIVEN_STORAGE_KEY);
    if (!raw) return result;

    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return result;

    for (const path of ALL_VICTORY_PRODUCT_IMAGES) {
      const rawValue = Number((parsed as Record<string, unknown>)[path]);
      if (Number.isFinite(rawValue) && rawValue >= 0) {
        result[path] = Math.floor(rawValue);
      }
    }
  } catch {
    // corrupt entry — fall back to zeros
  }

  return result;
}

function saveGivenCounts(counts: Record<string, number>) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(PRIZE_GIVEN_STORAGE_KEY, JSON.stringify(counts));
}

function pickWeightedPrize(
  victoryProducts: string[],
  stock: Record<string, number>,
): string | null {
  const candidates: { path: string; weight: number }[] = [];
  let totalWeight = 0;

  for (const path of victoryProducts) {
    const remaining = Math.max(0, Math.floor(stock[path] ?? 0));
    if (remaining <= 0) continue;
    candidates.push({ path, weight: remaining });
    totalWeight += remaining;
  }

  if (totalWeight <= 0) return null;

  let threshold = Math.random() * totalWeight;
  for (const candidate of candidates) {
    threshold -= candidate.weight;
    if (threshold <= 0) return candidate.path;
  }

  return candidates[candidates.length - 1].path;
}

function sanitizeConfig(input: unknown): GameConfig {
  const fallback = buildDefaultConfig();

  if (!input || typeof input !== "object") {
    return fallback;
  }

  const candidate = input as Partial<GameConfig>;
  const durationSeconds = clampValue(
    Number(candidate.durationSeconds) || fallback.durationSeconds,
    5,
    180,
  );
  const targetScore = clampValue(
    Number(candidate.targetScore) || fallback.targetScore,
    -100,
    500,
  );

  const legacySpawnIntervalMs = Number(
    (candidate as Record<string, unknown>).spawnIntervalMs,
  );

  const productsPerSecond = clampValue(
    Number(candidate.productsPerSecond) ||
      (Number.isFinite(legacySpawnIntervalMs)
        ? Number((1000 / legacySpawnIntervalMs).toFixed(1))
        : fallback.productsPerSecond),
    MIN_PRODUCTS_PER_SECOND,
    MAX_PRODUCTS_PER_SECOND,
  );

  const finalRushSeconds = clampValue(
    Number(candidate.finalRushSeconds) || fallback.finalRushSeconds,
    MIN_FINAL_RUSH_SECONDS,
    durationSeconds,
  );

  const finalRushSpeedMultiplier = clampValue(
    Number(candidate.finalRushSpeedMultiplier) ||
      fallback.finalRushSpeedMultiplier,
    MIN_FINAL_RUSH_SPEED_MULTIPLIER,
    MAX_FINAL_RUSH_SPEED_MULTIPLIER,
  );

  const rawGroups = Array.isArray(candidate.pointGroups)
    ? candidate.pointGroups
    : fallback.pointGroups;

  const pointGroups: PointGroupConfig[] = rawGroups
    .map((group, index) => {
      if (!group || typeof group !== "object") return null;

      const typedGroup = group as Partial<PointGroupConfig>;
      const products = Array.isArray(typedGroup.products)
        ? typedGroup.products
            .filter((path): path is string => {
              if (typeof path !== "string") return false;
              const migratedPath = migrateEscolheBemProductPath(path);
              return ALL_PRODUCT_IMAGES.includes(migratedPath);
            })
            .map((path) => migrateEscolheBemProductPath(path))
        : [];

      return {
        id:
          typeof typedGroup.id === "string" && typedGroup.id.trim().length > 0
            ? typedGroup.id
            : `group-${index + 1}`,
        points: clampValue(Number(typedGroup.points) || 0, -100, 100),
        products,
      };
    })
    .filter((group): group is PointGroupConfig => group !== null);

  const hasPlayablePointGroup = pointGroups.some(
    (group) => group.products.length > 0,
  );

  const safePointGroups = hasPlayablePointGroup
    ? pointGroups
    : fallback.pointGroups;

  const rawStock = (candidate as Record<string, unknown>).victoryProductStock;
  const victoryProductStock =
    rawStock && typeof rawStock === "object"
      ? buildEmptyStockMap()
      : buildDefaultStockMap();
  if (rawStock && typeof rawStock === "object") {
    for (const path of ALL_VICTORY_PRODUCT_IMAGES) {
      const storedValue = (rawStock as Record<string, unknown>)[path];
      // A victory product newly added since this config was saved won't have a
      // key here. Seed it with the default stock instead of 0, otherwise the new
      // prize is silently never awarded. An explicitly stored value (including a
      // deliberate 0) is still respected.
      if (storedValue === undefined) {
        victoryProductStock[path] = DEFAULT_VICTORY_PRODUCT_STOCK;
        continue;
      }
      const rawValue = Number(storedValue);
      if (Number.isFinite(rawValue) && rawValue >= 0) {
        victoryProductStock[path] = Math.floor(rawValue);
      }
    }
  }

  return {
    durationSeconds,
    targetScore,
    productsPerSecond,
    finalRushSeconds,
    finalRushSpeedMultiplier,
    pointGroups: safePointGroups,
    victoryProductStock,
  };
}

// perf: memoized so tap-driven parent re-renders skip reconciliation of HUD entries whose values didn't change
const HudItem = React.memo(function HudItem({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="escolhe-bem-hud-item">
      {label}
      {value}
    </div>
  );
});

// perf: memoized so the feedback bar only reconciles when its own inputs change; pointsFlashKey is hoisted to a `key` in the parent so animation still restarts per tap
const PointsFeedback = React.memo(function PointsFeedback({
  lastPoints,
  isRunning,
}: {
  lastPoints: number | null;
  isRunning: boolean;
}) {
  if (lastPoints === null && !isRunning) return null;

  const toneClass =
    lastPoints === null ? "neutral" : lastPoints >= 0 ? "positive" : "negative";

  return (
    <div className={`points-feedback ${toneClass}`}>
      {lastPoints === null
        ? "Toque nos produtos para pontuar"
        : `${formatSignedPoints(lastPoints)} pontos`}
    </div>
  );
});

export default function EscolheBemGame({ onBackToHome }: EscolheBemGameProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const hudRef = useRef<HTMLElement>(null);
  const arenaRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const frameRef = useRef<number | null>(null);
  const lastTimestampRef = useRef<number | null>(null);
  const spawnAccumulatorRef = useRef(0);
  const itemIdRef = useRef(0);
  const activeItemsRef = useRef<Map<number, FallingItem>>(new Map());
  // perf: pin decoded pixels in an ImageBitmap so Firefox can't evict and re-decode (DecodePool::SyncRunIfPossible) on the main thread
  const spriteCacheRef = useRef<Map<string, ImageBitmap>>(new Map());
  const scaledSpriteCacheRef = useRef<Map<string, HTMLCanvasElement>>(
    new Map(),
  );
  const pendingPointerRef = useRef<{ x: number; y: number } | null>(null);
  const removedItemBufferRef = useRef<number[]>([]);
  const arenaMetricsRef = useRef({
    width: window.innerWidth,
    height: window.innerHeight,
    itemSize: clampValue(
      window.innerWidth * ITEM_SIZE_RATIO,
      ITEM_SIZE_MIN,
      ITEM_SIZE_MAX,
    ),
  });
  // perf: fix #7 — track prior item size so we only clear scaled sprite cache when size actually changes
  const prevItemSizeRef = useRef<number>(-1);
  const feedbackTimerRef = useRef<number | null>(null);
  const feedbackSoundTimeoutsRef = useRef<number[]>([]);
  const managementTapCountRef = useRef(0);
  const managementTapTimeoutRef = useRef<number | null>(null);
  const scoreRef = useRef(0);
  const timeLeftRef = useRef(GAME_DURATION_SECONDS);
  const gameConfigRef = useRef(buildDefaultConfig());
  const audioContextRef = useRef<AudioContext | null>(null);
  // perf: fix #8 — ref mirror of playableGroups so buildSpawnItem stays stable across preload updates
  const playableGroupsRef = useRef<
    { id: string; points: number; products: string[] }[]
  >([]);
  // perf: fix #4 — debounce localStorage writes from admin keystrokes
  const configPersistTimeoutRef = useRef<number | null>(null);
  // perf: cache the 2D context so drawCanvasFrame doesn't re-query it every frame
  const canvasContextRef = useRef<CanvasRenderingContext2D | null>(null);
  // perf: cache arena bounds so pointerdown doesn't call getBoundingClientRect (potential forced reflow) on every tap
  const arenaBoundsRef = useRef<DOMRect | null>(null);
  // the victory product whose stock input is currently focused, so the sync
  // effect can refresh every other draft from config without stomping a value
  // the admin is mid-typing
  const editingStockPathRef = useRef<string | null>(null);

  const [gameStatus, setGameStatus] = useState<GameStatus>("idle");
  const [timeLeft, setTimeLeft] = useState(GAME_DURATION_SECONDS);
  const [score, setScore] = useState(0);
  const [lastPoints, setLastPoints] = useState<number | null>(null);
  const [pointsFlashKey, setPointsFlashKey] = useState(0);
  const [isManagementOpen, setIsManagementOpen] = useState(false);
  const [gameConfig, setGameConfig] = useState<GameConfig>(loadInitialConfig);
  const [loadedSpritePaths, setLoadedSpritePaths] = useState<Set<string>>(
    () => new Set(),
  );
  const [isPreloadingSprites, setIsPreloadingSprites] = useState(true);
  const [selectedVictoryProduct, setSelectedVictoryProduct] = useState<
    string | null
  >(null);
  const [givenCounts, setGivenCounts] = useState<Record<string, number>>(() =>
    loadGivenCounts(),
  );
  const [adminInputDrafts, setAdminInputDrafts] = useState({
    durationSeconds: `${GAME_DURATION_SECONDS}`,
    targetScore: `${TARGET_SCORE}`,
    productsPerSecond: `${DEFAULT_PRODUCTS_PER_SECOND}`,
    finalRushSeconds: `${DEFAULT_FINAL_RUSH_SECONDS}`,
    finalRushSpeedMultiplier: `${DEFAULT_FINAL_RUSH_SPEED_MULTIPLIER}`,
  });
  const [stockDrafts, setStockDrafts] = useState<Record<string, string>>(() => {
    const initialConfig = loadInitialConfig();
    const initial: Record<string, string> = {};
    for (const path of ALL_VICTORY_PRODUCT_IMAGES) {
      initial[path] = `${initialConfig.victoryProductStock[path] ?? 0}`;
    }
    return initial;
  });

  // Lockout state to prevent accidental taps after finishing
  const [isFinishLockActive, setIsFinishLockActive] = useState(false);

  const isRunning = gameStatus === "running";
  const didWin = gameStatus === "finished" && score >= gameConfig.targetScore;

  const hasConfiguredPointGroup = useMemo(
    () => gameConfig.pointGroups.some((group) => group.products.length > 0),
    [gameConfig.pointGroups],
  );

  const playableGroups = useMemo(
    () =>
      gameConfig.pointGroups
        .map((group) => ({
          ...group,
          products: group.products.filter((path) =>
            loadedSpritePaths.has(path),
          ),
        }))
        .filter((group) => group.products.length > 0),
    [gameConfig.pointGroups, loadedSpritePaths],
  );

  const canStartGame = !isPreloadingSprites && playableGroups.length > 0;
  const displayedVictoryProduct = selectedVictoryProduct;

  const getOrCreateAudioContext = useCallback(() => {
    if (audioContextRef.current) {
      return audioContextRef.current;
    }

    if (
      typeof window === "undefined" ||
      typeof window.AudioContext === "undefined"
    ) {
      return null;
    }

    audioContextRef.current = new window.AudioContext();
    return audioContextRef.current;
  }, []);

  const playTone = useCallback(
    (
      frequency: number,
      durationMs: number,
      oscillatorType: OscillatorType,
      volume = 0.15,
    ) => {
      const audioContext = getOrCreateAudioContext();
      if (!audioContext) return;

      const startTone = () => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        const durationSeconds = durationMs / 1000;
        const now = audioContext.currentTime;

        oscillator.type = oscillatorType;
        oscillator.frequency.setValueAtTime(frequency, now);

        gainNode.gain.setValueAtTime(0.0001, now);
        gainNode.gain.exponentialRampToValueAtTime(volume, now + 0.012);
        gainNode.gain.exponentialRampToValueAtTime(
          0.0001,
          now + durationSeconds,
        );

        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);

        oscillator.start(now);
        oscillator.stop(now + durationSeconds + 0.02);
      };

      if (audioContext.state === "suspended") {
        // perf: fix #9 — some browsers throw synchronously from resume(); guard both throw and rejected-promise paths
        try {
          void audioContext
            .resume()
            .then(startTone)
            .catch(() => undefined);
        } catch {
          // silently swallow audio errors
        }
        return;
      }

      startTone();
    },
    [getOrCreateAudioContext],
  );

  const playPointsFeedbackSound = useCallback(
    (points: number) => {
      if (points > 0) {
        playTone(900, 90, "triangle", 0.14);
        const timeoutId = window.setTimeout(() => {
          playTone(1200, 110, "triangle", 0.14);
        }, 85);
        feedbackSoundTimeoutsRef.current.push(timeoutId);
        return;
      }

      if (points < 0) {
        playTone(260, 120, "sawtooth", 0.13);
        const timeoutId = window.setTimeout(() => {
          playTone(170, 150, "sawtooth", 0.13);
        }, 95);
        feedbackSoundTimeoutsRef.current.push(timeoutId);
      }
    },
    [playTone],
  );

  const syncTrackInsets = useCallback(() => {
    const rootElement = rootRef.current;
    const hudElement = hudRef.current;
    if (!rootElement || !hudElement) return;

    const hudItems = hudElement.querySelectorAll<HTMLElement>(
      ".escolhe-bem-hud-item",
    );
    if (hudItems.length === 0) return;

    const firstItemRect = hudItems[0].getBoundingClientRect();
    const lastItemRect = hudItems[hudItems.length - 1].getBoundingClientRect();
    const rootRect = rootElement.getBoundingClientRect();
    const rootStyles = window.getComputedStyle(rootElement);
    const rootPaddingLeft = Number.parseFloat(rootStyles.paddingLeft) || 0;
    const rootPaddingRight = Number.parseFloat(rootStyles.paddingRight) || 0;

    const leftInset = Math.max(
      0,
      firstItemRect.left - rootRect.left - rootPaddingLeft,
    );
    const rightInset = Math.max(
      0,
      rootRect.right - lastItemRect.right - rootPaddingRight,
    );

    rootElement.style.setProperty(
      "--escolhe-bem-track-inset-left",
      `${leftInset}px`,
    );
    rootElement.style.setProperty(
      "--escolhe-bem-track-inset-right",
      `${rightInset}px`,
    );
  }, []);

  const syncCanvasSize = useCallback(() => {
    const canvasElement = canvasRef.current;
    const arenaElement = arenaRef.current;
    if (!canvasElement || !arenaElement) return;

    const width = Math.max(1, arenaElement.clientWidth);
    const height = Math.max(1, arenaElement.clientHeight);
    const itemSize = clampValue(
      width * ITEM_SIZE_RATIO,
      ITEM_SIZE_MIN,
      ITEM_SIZE_MAX,
    );

    arenaMetricsRef.current = {
      width,
      height,
      itemSize,
    };

    // perf: fix #7 — only re-rasterize sprites when rounded item size actually changed
    const roundedItemSize = Math.round(itemSize);
    if (roundedItemSize !== prevItemSizeRef.current) {
      scaledSpriteCacheRef.current.clear();
      // perf: invalidate per-item scaled sprite caches so drawCanvasFrame re-resolves them at the new size
      activeItemsRef.current.forEach((item) => {
        item.scaledSprite = null;
      });
      prevItemSizeRef.current = roundedItemSize;
    }

    const pixelRatio = Math.min(
      window.devicePixelRatio || 1,
      MAX_CANVAS_PIXEL_RATIO,
    );

    canvasElement.style.width = `${width}px`;
    canvasElement.style.height = `${height}px`;
    canvasElement.width = Math.floor(width * pixelRatio);
    canvasElement.height = Math.floor(height * pixelRatio);

    const context = canvasElement.getContext("2d");
    if (!context) return;

    // perf: store the 2D context so drawCanvasFrame can skip getContext() calls each frame
    canvasContextRef.current = context;

    context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    // perf: sprites are pre-rasterized at target size and drawn at integer positions 1:1 — no scaling happens at drawImage time, so smoothing is pure overhead
    context.imageSmoothingEnabled = false;

    // perf: refresh cached arena bounds whenever layout changes
    arenaBoundsRef.current = arenaElement.getBoundingClientRect();
  }, []);

  const getScaledSprite = useCallback((imagePath: string, itemSize: number) => {
    const sprite = spriteCacheRef.current.get(imagePath);
    if (!sprite) return null;

    const roundedItemSize = Math.max(1, Math.round(itemSize));
    const cacheKey = `${imagePath}@${roundedItemSize}`;
    const cachedScaledSprite = scaledSpriteCacheRef.current.get(cacheKey);
    if (cachedScaledSprite) {
      return cachedScaledSprite;
    }

    const scaledCanvas = document.createElement("canvas");
    scaledCanvas.width = roundedItemSize;
    scaledCanvas.height = roundedItemSize;

    const scaledContext = scaledCanvas.getContext("2d");
    if (!scaledContext) return null;

    scaledContext.imageSmoothingEnabled = true;
    scaledContext.imageSmoothingQuality = "low";
    scaledContext.clearRect(0, 0, roundedItemSize, roundedItemSize);
    scaledContext.drawImage(sprite, 0, 0, roundedItemSize, roundedItemSize);

    scaledSpriteCacheRef.current.set(cacheKey, scaledCanvas);
    return scaledCanvas;
  }, []);

  const drawCanvasFrame = useCallback(() => {
    const canvasElement = canvasRef.current;
    if (!canvasElement) return;

    // perf: prefer the cached context; fall back to getContext() the first time (before syncCanvasSize has populated it)
    let context = canvasContextRef.current;
    if (!context) {
      context = canvasElement.getContext("2d");
      if (!context) return;
      canvasContextRef.current = context;
    }

    const { width, height, itemSize } = arenaMetricsRef.current;
    context.clearRect(0, 0, width, height);

    // perf: hoist loop invariants — all items in a frame share the same size/half-size
    const drawSize = Math.round(itemSize);
    const halfSize = drawSize / 2;

    // perf: snapshot to array + for...of instead of Map.forEach to avoid per-frame closure allocation
    const activeItems = Array.from(activeItemsRef.current.values());
    for (const item of activeItems) {
      // perf: prefer per-item cached sprite; only fall back to getScaledSprite when null (first draw or post-resize)
      let scaledSprite = item.scaledSprite;
      if (!scaledSprite) {
        scaledSprite = getScaledSprite(item.imagePath, itemSize);
        if (!scaledSprite) continue;
        item.scaledSprite = scaledSprite;
      }

      const xPx = (item.xPercent / 100) * width;
      if (item.rotation === 0) {
        // perf: snap draw position to integer pixels to avoid sub-pixel bilinear filtering on every frame
        context.drawImage(
          scaledSprite,
          (xPx - halfSize) | 0,
          item.y | 0,
          drawSize,
          drawSize,
        );
        continue;
      }

      const centerY = item.y + halfSize;
      const rotationRadians = (item.rotation * Math.PI) / 180;

      context.save();
      context.translate(xPx, centerY);
      context.rotate(rotationRadians);
      context.drawImage(scaledSprite, -halfSize, -halfSize, drawSize, drawSize);
      context.restore();
    }
  }, [getScaledSprite]);

  // perf: fix #4 — debounce admin-keystroke-driven config writes to avoid sync JSON.stringify + disk writes per keystroke
  useEffect(() => {
    if (configPersistTimeoutRef.current !== null) {
      window.clearTimeout(configPersistTimeoutRef.current);
    }
    configPersistTimeoutRef.current = window.setTimeout(() => {
      localStorage.setItem(CONFIG_STORAGE_KEY, JSON.stringify(gameConfig));
      configPersistTimeoutRef.current = null;
    }, 800);

    return () => {
      if (configPersistTimeoutRef.current !== null) {
        window.clearTimeout(configPersistTimeoutRef.current);
        configPersistTimeoutRef.current = null;
      }
    };
  }, [gameConfig]);

  useEffect(() => {
    scoreRef.current = score;
  }, [score]);

  useEffect(() => {
    timeLeftRef.current = timeLeft;
  }, [timeLeft]);

  useEffect(() => {
    gameConfigRef.current = gameConfig;
  }, [gameConfig]);

  // perf: fix #8 — mirror playableGroups into a ref so buildSpawnItem doesn't churn during preload
  useEffect(() => {
    playableGroupsRef.current = playableGroups;
  }, [playableGroups]);

  useEffect(() => {
    setAdminInputDrafts({
      durationSeconds: `${gameConfig.durationSeconds}`,
      targetScore: `${gameConfig.targetScore}`,
      productsPerSecond: `${gameConfig.productsPerSecond}`,
      finalRushSeconds: `${gameConfig.finalRushSeconds}`,
      finalRushSpeedMultiplier: `${gameConfig.finalRushSpeedMultiplier}`,
    });
  }, [
    gameConfig.durationSeconds,
    gameConfig.finalRushSeconds,
    gameConfig.finalRushSpeedMultiplier,
    gameConfig.productsPerSecond,
    gameConfig.targetScore,
  ]);

  // mirror stock into the draft strings, but only for products whose draft is
  // already in sync — so we don't stomp a value the admin is mid-typing
  useEffect(() => {
    setStockDrafts((previousDrafts) => {
      let nextDrafts = previousDrafts;
      for (const path of ALL_VICTORY_PRODUCT_IMAGES) {
        // skip only the input the admin is actively editing, so we don't stomp
        // a value mid-typing; every other draft must reflect the real config
        if (editingStockPathRef.current === path) continue;
        const stockValue = gameConfig.victoryProductStock[path] ?? 0;
        const stockString = `${stockValue}`;
        if (previousDrafts[path] === stockString) continue;
        if (nextDrafts === previousDrafts) {
          nextDrafts = { ...previousDrafts };
        }
        nextDrafts[path] = stockString;
      }
      return nextDrafts;
    });
  }, [gameConfig.victoryProductStock]);

  useEffect(() => {
    const handleResize = () => {
      syncCanvasSize();
      drawCanvasFrame();
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [drawCanvasFrame, syncCanvasSize]);

  // perf: fix #8 — read playable groups from ref so this callback is stable across preload progress updates
  const buildSpawnItem = useCallback((): FallingItem | null => {
    const currentPlayableGroups = playableGroupsRef.current;
    if (currentPlayableGroups.length === 0) return null;

    const selectedGroup = getRandomFrom(currentPlayableGroups);
    const imagePath = getRandomFrom(selectedGroup.products);
    const nextId = itemIdRef.current++;

    const newItem: FallingItem = {
      id: nextId,
      imagePath,
      points: selectedGroup.points,
      xPercent: 8 + Math.random() * 84,
      y: -110,
      speed: getItemSpeed(),
      rotation: ENABLE_ITEM_ROTATION ? -15 + Math.random() * 30 : 0,
      scaledSprite: null,
    };

    return newItem;
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function preloadSprites() {
      setIsPreloadingSprites(true);
      setLoadedSpritePaths(new Set());
      // perf: close previously decoded bitmaps before clearing, otherwise their pinned pixel buffers leak across remounts
      spriteCacheRef.current.forEach((bitmap) => bitmap.close());
      spriteCacheRef.current.clear();
      scaledSpriteCacheRef.current.clear();

      const uniquePaths = Array.from(new Set(ALL_PRODUCT_IMAGES));
      await Promise.all(
        uniquePaths.map(
          (imagePath) =>
            new Promise<void>((resolve) => {
              const optimizedPath = toOptimizedEscolheBemProductPath(imagePath);
              const originalPath = toOriginalEscolheBemProductPath(imagePath);
              const imageElement = new Image();
              let didTryFallback = false;

              const decodeAndStore = async () => {
                try {
                  // perf: createImageBitmap off the event loop; the resulting ImageBitmap is decoded once and pinned, so later drawImage calls never re-decode
                  const bitmap = await createImageBitmap(imageElement);
                  if (cancelled) {
                    bitmap.close();
                    return;
                  }
                  spriteCacheRef.current.set(originalPath, bitmap);
                  setLoadedSpritePaths((currentPaths) => {
                    if (currentPaths.has(originalPath)) {
                      return currentPaths;
                    }

                    const nextPaths = new Set(currentPaths);
                    nextPaths.add(originalPath);
                    return nextPaths;
                  });
                  drawCanvasFrame();
                } catch {
                  // createImageBitmap failed (rare: unsupported format / decode error) — sprite stays unloaded, playable groups filter it out
                }
              };

              imageElement.onload = () => {
                void decodeAndStore().finally(resolve);
              };

              imageElement.onerror = () => {
                if (didTryFallback) {
                  resolve();
                  return;
                }

                didTryFallback = true;
                imageElement.src = resolvePublicAssetPath(originalPath);
              };

              imageElement.src = resolvePublicAssetPath(optimizedPath);
            }),
        ),
      );

      if (!cancelled) {
        setIsPreloadingSprites(false);
      }
    }

    preloadSprites();

    return () => {
      cancelled = true;
    };
  }, [drawCanvasFrame]);

  // Victory/prize images are shown via <img>, not the canvas, so they're not
  // part of the sprite preload above. On the offline totem the service worker's
  // CacheFirst image route can only serve what was fetched while online, so a
  // prize that was never displayed before stays blank. Warm the cache for every
  // victory image (optimized + original fallback) up front using new Image(),
  // which routes through the SW exactly like the real <img> will.
  useEffect(() => {
    const warmedImages: HTMLImageElement[] = [];

    for (const productPath of ALL_VICTORY_PRODUCT_IMAGES) {
      for (const candidatePath of [
        toOptimizedEscolheBemProductPath(productPath),
        toOriginalEscolheBemProductPath(productPath),
      ]) {
        const image = new Image();
        image.src = resolvePublicAssetPath(candidatePath);
        warmedImages.push(image);
      }
    }

    return () => {
      // drop refs so the decoded images can be GC'd; the SW cache entry persists
      warmedImages.length = 0;
    };
  }, []);

  const clearActiveItems = useCallback(() => {
    activeItemsRef.current.clear();
    drawCanvasFrame();
  }, [drawCanvasFrame]);

  const removeItemsById = useCallback(
    (itemIds: number[], shouldDraw = true) => {
      if (itemIds.length === 0) return;

      for (const itemId of itemIds) {
        activeItemsRef.current.delete(itemId);
      }

      if (shouldDraw) {
        drawCanvasFrame();
      }
    },
    [drawCanvasFrame],
  );

  const finishGame = useCallback(() => {
    const currentConfig = gameConfigRef.current;
    const didReachTarget = scoreRef.current >= currentConfig.targetScore;
    const eligiblePrizes = getEligibleVictoryProducts(
      currentConfig.victoryProductStock,
    );

    if (didReachTarget && eligiblePrizes.length > 0) {
      const prize = pickWeightedPrize(
        eligiblePrizes,
        currentConfig.victoryProductStock,
      );
      setSelectedVictoryProduct(prize);

      if (prize) {
        setGameConfig((previousConfig) => {
          const currentStock = Math.floor(
            previousConfig.victoryProductStock[prize] ?? 0,
          );
          const nextStockValue = Math.max(0, currentStock - 1);
          const nextConfig: GameConfig = {
            ...previousConfig,
            victoryProductStock: {
              ...previousConfig.victoryProductStock,
              [prize]: nextStockValue,
            },
          };
          // flush sync so a tablet reload between draws can't roll the decrement back
          window.localStorage.setItem(
            CONFIG_STORAGE_KEY,
            JSON.stringify(nextConfig),
          );
          return nextConfig;
        });

        setGivenCounts((previousCounts) => {
          const nextCounts = {
            ...previousCounts,
            [prize]: (previousCounts[prize] ?? 0) + 1,
          };
          saveGivenCounts(nextCounts);
          return nextCounts;
        });
      }
    } else {
      setSelectedVictoryProduct(null);
    }

    setGameStatus("finished");
    clearActiveItems();

    // Activate lockout for a short period to prevent accidental taps
    setIsFinishLockActive(true);
    setTimeout(() => setIsFinishLockActive(false), 500); // 500ms lock
  }, [clearActiveItems]);

  const startGame = useCallback(() => {
    if (!canStartGame) return;

    itemIdRef.current = 0;
    spawnAccumulatorRef.current = 0;
    lastTimestampRef.current = null;
    setScore(0);
    setLastPoints(null);
    setSelectedVictoryProduct(null);
    setTimeLeft(gameConfig.durationSeconds);
    clearActiveItems();
    syncCanvasSize();
    drawCanvasFrame();
    setGameStatus("running");

    if (feedbackTimerRef.current !== null) {
      window.clearTimeout(feedbackTimerRef.current);
      feedbackTimerRef.current = null;
    }
  }, [
    canStartGame,
    clearActiveItems,
    drawCanvasFrame,
    gameConfig.durationSeconds,
    syncCanvasSize,
  ]);

  const resetToIdle = useCallback(() => {
    setGameStatus("idle");
    setScore(0);
    setTimeLeft(gameConfig.durationSeconds);
    setLastPoints(null);
    setSelectedVictoryProduct(null);
    clearActiveItems();
  }, [clearActiveItems, gameConfig.durationSeconds]);

  const resetToDefaultConfig = () => {
    setGameConfig(buildDefaultConfig());
    resetToIdle();
  };

  const updateNumericConfig = (
    field:
      | "durationSeconds"
      | "targetScore"
      | "productsPerSecond"
      | "finalRushSeconds"
      | "finalRushSpeedMultiplier",
    value: number,
  ) => {
    const getClampedValue = (rawValue: number, maxFinalRushSeconds: number) => {
      if (field === "durationSeconds") {
        return clampValue(rawValue, 5, 180);
      }

      if (field === "targetScore") {
        return clampValue(rawValue, -100, 500);
      }

      if (field === "finalRushSeconds") {
        return clampValue(
          rawValue,
          MIN_FINAL_RUSH_SECONDS,
          maxFinalRushSeconds,
        );
      }

      if (field === "finalRushSpeedMultiplier") {
        return clampValue(
          rawValue,
          MIN_FINAL_RUSH_SPEED_MULTIPLIER,
          MAX_FINAL_RUSH_SPEED_MULTIPLIER,
        );
      }

      return clampValue(
        rawValue,
        MIN_PRODUCTS_PER_SECOND,
        MAX_PRODUCTS_PER_SECOND,
      );
    };

    setGameConfig((currentConfig) => {
      const nextConfig = { ...currentConfig };
      const maxFinalRushSeconds =
        field === "durationSeconds"
          ? clampValue(value, 5, 180)
          : nextConfig.durationSeconds;

      nextConfig[field] = getClampedValue(value, maxFinalRushSeconds);
      nextConfig.finalRushSeconds = clampValue(
        nextConfig.finalRushSeconds,
        MIN_FINAL_RUSH_SECONDS,
        nextConfig.durationSeconds,
      );

      return nextConfig;
    });
  };

  const commitNumericDraft = (
    field:
      | "durationSeconds"
      | "targetScore"
      | "productsPerSecond"
      | "finalRushSeconds"
      | "finalRushSpeedMultiplier",
  ) => {
    const rawValue = adminInputDrafts[field].trim();
    const currentValue = gameConfig[field];

    if (rawValue.length === 0) {
      setAdminInputDrafts((currentDrafts) => ({
        ...currentDrafts,
        [field]: `${currentValue}`,
      }));
      return;
    }

    const parsedValue = Number(rawValue);
    if (Number.isNaN(parsedValue)) {
      setAdminInputDrafts((currentDrafts) => ({
        ...currentDrafts,
        [field]: `${currentValue}`,
      }));
      return;
    }

    updateNumericConfig(field, parsedValue);
  };

  const updateGroupPoints = (groupId: string, points: number) => {
    setGameConfig((currentConfig) => ({
      ...currentConfig,
      pointGroups: currentConfig.pointGroups.map((group) =>
        group.id === groupId
          ? { ...group, points: clampValue(points, -100, 100) }
          : group,
      ),
    }));
  };

  const toggleProductInGroup = (groupId: string, productPath: string) => {
    setGameConfig((currentConfig) => {
      const targetGroup = currentConfig.pointGroups.find(
        (group) => group.id === groupId,
      );

      if (!targetGroup) return currentConfig;

      const isAlreadyInTarget = targetGroup.products.includes(productPath);

      return {
        ...currentConfig,
        pointGroups: currentConfig.pointGroups.map((group) => {
          const withoutProduct = group.products.filter(
            (path) => path !== productPath,
          );

          if (group.id !== groupId) {
            return { ...group, products: withoutProduct };
          }

          if (isAlreadyInTarget) {
            return { ...group, products: withoutProduct };
          }

          return { ...group, products: [...withoutProduct, productPath] };
        }),
      };
    });
  };

  const addPointGroup = () => {
    setGameConfig((currentConfig) => ({
      ...currentConfig,
      pointGroups: [
        ...currentConfig.pointGroups,
        {
          id: `group-${Date.now()}`,
          points: 0,
          products: [],
        },
      ],
    }));
  };

  const updateVictoryProductStock = (productPath: string, value: number) => {
    const safeValue =
      Number.isFinite(value) && value >= 0 ? Math.floor(value) : 0;
    setGameConfig((currentConfig) => ({
      ...currentConfig,
      victoryProductStock: {
        ...currentConfig.victoryProductStock,
        [productPath]: safeValue,
      },
    }));
  };

  const commitStockDraft = (productPath: string) => {
    const rawValue = (stockDrafts[productPath] ?? "").trim();
    const currentStock = gameConfig.victoryProductStock[productPath] ?? 0;

    if (rawValue === "") {
      setStockDrafts((previousDrafts) => ({
        ...previousDrafts,
        [productPath]: `${currentStock}`,
      }));
      return;
    }

    const parsed = Number(rawValue);
    if (!Number.isFinite(parsed) || parsed < 0) {
      setStockDrafts((previousDrafts) => ({
        ...previousDrafts,
        [productPath]: `${currentStock}`,
      }));
      return;
    }

    const safeValue = Math.floor(parsed);
    setStockDrafts((previousDrafts) => ({
      ...previousDrafts,
      [productPath]: `${safeValue}`,
    }));
    updateVictoryProductStock(productPath, safeValue);
  };

  const resetGivenCounts = () => {
    const confirmed = window.confirm(
      "Repor a contagem de produtos entregues neste dispositivo?",
    );
    if (!confirmed) return;
    const emptyCounts = buildEmptyStockMap();
    setGivenCounts(emptyCounts);
    saveGivenCounts(emptyCounts);
  };

  const removePointGroup = (groupId: string) => {
    setGameConfig((currentConfig) => {
      if (currentConfig.pointGroups.length <= 1) return currentConfig;

      return {
        ...currentConfig,
        pointGroups: currentConfig.pointGroups.filter(
          (group) => group.id !== groupId,
        ),
      };
    });
  };

  const openManagementMenu = () => {
    if (gameStatus === "running") {
      resetToIdle();
    }
    setIsManagementOpen(true);
  };

  const handleManagementLogoClick = () => {
    managementTapCountRef.current += 1;

    if (managementTapTimeoutRef.current !== null) {
      window.clearTimeout(managementTapTimeoutRef.current);
    }

    managementTapTimeoutRef.current = window.setTimeout(() => {
      managementTapCountRef.current = 0;
    }, MANAGEMENT_TAP_WINDOW_MS);

    if (managementTapCountRef.current >= MANAGEMENT_TAP_COUNT) {
      managementTapCountRef.current = 0;
      if (managementTapTimeoutRef.current !== null) {
        window.clearTimeout(managementTapTimeoutRef.current);
        managementTapTimeoutRef.current = null;
      }
      openManagementMenu();
    }
  };

  const handleItemClick = useCallback(
    (itemId: number) => {
      if (!isRunning) return;

      const clickedItem = activeItemsRef.current.get(itemId);
      if (!clickedItem) return;

      // perf: fix #6 — keep scoreRef in sync with the score delta immediately, so finishGame never reads a stale value if the game ends in the same frame
      scoreRef.current = scoreRef.current + clickedItem.points;

      // perf: fix #5 — React 19 + createRoot (src/index.tsx) gives automatic batching, so these three setStates already coalesce into one render
      setScore((currentScore) => currentScore + clickedItem.points);
      setLastPoints(clickedItem.points);
      setPointsFlashKey((currentKey) => currentKey + 1);
      playPointsFeedbackSound(clickedItem.points);

      if (feedbackTimerRef.current !== null) {
        window.clearTimeout(feedbackTimerRef.current);
      }

      feedbackTimerRef.current = window.setTimeout(() => {
        setLastPoints(null);
        feedbackTimerRef.current = null;
      }, POINTS_FEEDBACK_DURATION_MS);

      removeItemsById([itemId], false);
    },
    [isRunning, playPointsFeedbackSound, removeItemsById],
  );

  // perf: fix #10 — attached via addEventListener with { passive: false } in a useEffect below; keep as a plain DOM handler
  const handleArenaPointerDown = useCallback(
    (event: PointerEvent) => {
      if (!isRunning) return;

      const arenaElement = arenaRef.current;
      if (!arenaElement) return;

      // perf: use cached bounds refreshed on resize to avoid a potential forced reflow per tap; fall back only on the very first tap before syncCanvasSize has run
      let arenaBounds = arenaBoundsRef.current;
      if (!arenaBounds) {
        arenaBounds = arenaElement.getBoundingClientRect();
        arenaBoundsRef.current = arenaBounds;
      }
      pendingPointerRef.current = {
        x: event.clientX - arenaBounds.left,
        y: event.clientY - arenaBounds.top,
      };
      event.preventDefault();
    },
    [isRunning],
  );

  // perf: fix #10 — register pointerdown as non-passive explicitly so React's synthetic event system doesn't emit passive-listener warnings; game is full-screen so scroll isn't needed
  useEffect(() => {
    const arenaElement = arenaRef.current;
    if (!arenaElement) return;

    arenaElement.addEventListener("pointerdown", handleArenaPointerDown, {
      passive: false,
    });

    return () => {
      arenaElement.removeEventListener("pointerdown", handleArenaPointerDown);
    };
  }, [handleArenaPointerDown]);

  useEffect(() => {
    if (!isRunning) return;

    const timer = window.setInterval(() => {
      setTimeLeft((currentTime) => {
        if (currentTime <= 1) {
          window.clearInterval(timer);
          finishGame();
          return 0;
        }
        return currentTime - 1;
      });
    }, 1000);

    return () => {
      window.clearInterval(timer);
    };
  }, [finishGame, isRunning]);

  useEffect(() => {
    syncTrackInsets();

    if (typeof ResizeObserver !== "undefined") {
      const rootElement = rootRef.current;
      const hudElement = hudRef.current;
      if (!rootElement || !hudElement) return;

      const resizeObserver = new ResizeObserver(() => {
        syncTrackInsets();
      });

      resizeObserver.observe(rootElement);
      resizeObserver.observe(hudElement);
      hudElement
        .querySelectorAll<HTMLElement>(".escolhe-bem-hud-item")
        .forEach((itemElement) => {
          resizeObserver.observe(itemElement);
        });

      return () => {
        resizeObserver.disconnect();
      };
    }

    const handleResize = () => {
      syncTrackInsets();
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
    // perf: fix #3 — score/timeLeft/targetScore are HUD-only display state; they don't affect layout, so they should not re-subscribe the ResizeObserver
  }, [syncTrackInsets]);

  useEffect(() => {
    if (!isRunning) {
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
      }
      frameRef.current = null;
      return;
    }

    const tick = (timestamp: number) => {
      if (lastTimestampRef.current === null) {
        lastTimestampRef.current = timestamp;
        frameRef.current = requestAnimationFrame(tick);
        return;
      }

      const rawDeltaMs = timestamp - lastTimestampRef.current;

      const deltaMs = Math.min(rawDeltaMs, MAX_DELTA_MS);
      lastTimestampRef.current = timestamp;
      spawnAccumulatorRef.current += deltaMs;

      // perf: fix #2 — read spawn rate from the ref (kept in sync by a separate effect) so this rAF loop doesn't tear down on config changes
      const spawnIntervalMs = productsPerSecondToInterval(
        gameConfigRef.current.productsPerSecond,
      );

      const dueSpawns = Math.floor(
        spawnAccumulatorRef.current / spawnIntervalMs,
      );
      if (dueSpawns > 0) {
        spawnAccumulatorRef.current -= dueSpawns * spawnIntervalMs;
      }

      const arenaHeight = arenaMetricsRef.current.height;
      const arenaWidth = arenaMetricsRef.current.width;
      const itemSize = arenaMetricsRef.current.itemSize;

      const pendingPointer = pendingPointerRef.current;
      if (pendingPointer) {
        pendingPointerRef.current = null;

        // perf: fix #1 — break on first hit instead of scanning every item; forEach can't break, so snapshot to array and for...of
        let hitItemId: number | null = null;
        const itemsSnapshot = Array.from(activeItemsRef.current.values());
        for (const item of itemsSnapshot) {
          const centerX = (item.xPercent / 100) * arenaWidth;
          const left = centerX - itemSize / 2;
          const top = item.y;
          const right = left + itemSize;
          const bottom = top + itemSize;

          const isInsideItem =
            pendingPointer.x >= left &&
            pendingPointer.x <= right &&
            pendingPointer.y >= top &&
            pendingPointer.y <= bottom;

          if (isInsideItem) {
            hitItemId = item.id;
            break;
          }
        }

        if (hitItemId !== null) {
          handleItemClick(hitItemId);
        }
      }

      const removedItemIds = removedItemBufferRef.current;
      removedItemIds.length = 0;

      // perf: hoist loop invariants — final-rush state, speed multiplier, and deltaSeconds are identical for every item in a frame
      const currentConfig = gameConfigRef.current;
      const isFinalRush = timeLeftRef.current <= currentConfig.finalRushSeconds;
      const speedMultiplier = isFinalRush
        ? currentConfig.finalRushSpeedMultiplier
        : 1;
      const deltaSeconds = deltaMs / 1000;
      const offscreenThreshold = arenaHeight + 120;

      // perf: snapshot values for for...of; still read ids from item.id so we can push into the removal buffer
      const updatingItems = Array.from(activeItemsRef.current.values());
      for (const item of updatingItems) {
        item.y += item.speed * speedMultiplier * deltaSeconds;

        if (item.y >= offscreenThreshold) {
          removedItemIds.push(item.id);
        }
      }

      if (removedItemIds.length > 0) {
        removeItemsById(removedItemIds, false);
      }

      const maxActiveItemsForArena = getDynamicMaxActiveItems(
        arenaWidth,
        arenaHeight,
      );

      const availableSlots = Math.max(
        0,
        maxActiveItemsForArena - activeItemsRef.current.size,
      );
      const candidateSpawnCount = Math.min(
        dueSpawns,
        MAX_SPAWNS_PER_FRAME,
        availableSlots,
      );

      if (candidateSpawnCount > 0) {
        for (let index = 0; index < candidateSpawnCount; index += 1) {
          const nextItem = buildSpawnItem();
          if (!nextItem) continue;

          activeItemsRef.current.set(nextItem.id, nextItem);
        }
      }

      drawCanvasFrame();

      frameRef.current = requestAnimationFrame(tick);
    };

    frameRef.current = requestAnimationFrame(tick);

    return () => {
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
      }
      frameRef.current = null;
      lastTimestampRef.current = null;
    };
    // perf: fix #2 — gameConfig.productsPerSecond intentionally omitted; read via gameConfigRef inside the tick so changing spawn rate doesn't tear down the rAF loop
  }, [
    buildSpawnItem,
    drawCanvasFrame,
    handleItemClick,
    isRunning,
    removeItemsById,
  ]);

  useEffect(() => {
    // perf: capture refs at effect-creation time so the cleanup closure doesn't follow mutations across remounts
    const spriteCache = spriteCacheRef.current;
    const scaledSpriteCache = scaledSpriteCacheRef.current;

    return () => {
      feedbackSoundTimeoutsRef.current.forEach((timeoutId) => {
        window.clearTimeout(timeoutId);
      });
      feedbackSoundTimeoutsRef.current = [];

      if (feedbackTimerRef.current !== null) {
        window.clearTimeout(feedbackTimerRef.current);
      }

      if (managementTapTimeoutRef.current !== null) {
        window.clearTimeout(managementTapTimeoutRef.current);
      }

      if (audioContextRef.current) {
        void audioContextRef.current.close();
        audioContextRef.current = null;
      }

      // perf: release pinned decoded-bitmap buffers; HTMLCanvasElement entries GC normally
      spriteCache.forEach((bitmap) => bitmap.close());
      spriteCache.clear();
      scaledSpriteCache.clear();
    };
  }, []);

  useEffect(() => {
    setTimeLeft((currentTime) => {
      if (isRunning) return currentTime;
      return gameConfig.durationSeconds;
    });
  }, [gameConfig.durationSeconds, isRunning]);

  if (!isTablet()) {
    return (
      <div className="unsupported-device">
        <button
          className="back-button"
          onClick={onBackToHome}
          title="Voltar ao início">
          ← Voltar
        </button>
        Este jogo está disponível apenas em tablets.
        <br />
        Aceda através de um dispositivo compatível ou utilize a emulação de
        dispositivo no navegador.
      </div>
    );
  }

  return (
    <div
      className={`escolhe-bem ${isManagementOpen ? "management-open" : ""}`}
      ref={rootRef}>
      <button
        className="back-button"
        onClick={onBackToHome}
        title="Voltar ao início">
        ← Voltar
      </button>

      <header className="escolhe-bem-header">
        <button
          type="button"
          className="escolhe-bem-logo-button"
          onClick={handleManagementLogoClick}
          title="Clique 5 vezes para abrir gestão">
          <img
            src={resolvePublicAssetPath("/images/joga-e-ganha-transparent.png")}
            alt="Logo Joga e Ganha"
            className="escolhe-bem-top-logo"
          />
        </button>
        <h1 className="escolhe-bem-title">
          <img
            src={resolvePublicAssetPath("/images/recarrega-te.png")}
            alt="Recarrega-te"
            className="escolhe-bem-title-image"
          />
        </h1>
      </header>

      <section className="escolhe-bem-hud" ref={hudRef}>
        <HudItem label="Pontos: " value={score} />
        <HudItem label="Tempo: " value={`${timeLeft}s`} />
        <HudItem label="Objetivo: " value={gameConfig.targetScore} />
      </section>

      <section className="escolhe-bem-arena" ref={arenaRef}>
        {/* perf: fix #10 — pointerdown is registered via addEventListener with { passive: false } in a useEffect */}
        <canvas className="escolhe-bem-canvas" ref={canvasRef} />

        {gameStatus === "idle" ? (
          <div className="escolhe-bem-overlay escolhe-bem-overlay-idle">
            <div className="escolhe-bem-idle-content">
              <h2>Preparado para jogar?</h2>
              <p>
                Tem {gameConfig.durationSeconds} segundos para atingir{" "}
                {gameConfig.targetScore} pontos ou mais.
              </p>
              {isPreloadingSprites ? (
                <p>A carregar produtos do jogo...</p>
              ) : !hasConfiguredPointGroup ? (
                <p>
                  Configure pelo menos um produto no menu de gestão para
                  começar.
                </p>
              ) : !canStartGame ? (
                <p>
                  Aguarde: os produtos selecionados ainda não estão
                  disponíveis.
                </p>
              ) : null}
              <button
                type="button"
                onClick={startGame}
                disabled={!canStartGame}>
                Começar jogo
              </button>
            </div>
            <img
              src={resolvePublicAssetPath("/images/alimenta-a-pedalada.png")}
              alt=""
              className="escolhe-bem-idle-image"
            />
          </div>
        ) : null}

        {gameStatus === "finished" ? (
          <div className="escolhe-bem-overlay">
            <h2>{didWin ? "Vitória!" : "Não foi desta"}</h2>
            {didWin ? (
              <>
                <p>Pontuação final: {score} (objetivo cumprido)</p>
                {displayedVictoryProduct ? (
                  <img
                    src={resolvePublicAssetPath(
                      toOptimizedEscolheBemProductPath(displayedVictoryProduct),
                    )}
                    onError={(event) =>
                      handleProductImageLoadError(
                        event,
                        toOriginalEscolheBemProductPath(displayedVictoryProduct),
                      )
                    }
                    alt="Produto prémio"
                    className="escolhe-bem-victory-image"
                  />
                ) : null}
              </>
            ) : (
              <p>Tem de fazer melhores escolhas! Volte mais tarde.</p>
            )}
            <button
              type="button"
              onClick={isFinishLockActive ? undefined : startGame}
              disabled={isFinishLockActive}
              style={
                isFinishLockActive
                  ? { opacity: 0.6, pointerEvents: "none" }
                  : {}
              }>
              Jogar novamente
            </button>
            <button
              type="button"
              className="secondary"
              onClick={isFinishLockActive ? undefined : resetToIdle}
              disabled={isFinishLockActive}
              style={
                isFinishLockActive
                  ? { opacity: 0.6, pointerEvents: "none" }
                  : {}
              }>
              Voltar ao ecrã inicial
            </button>
          </div>
        ) : null}
      </section>

      <footer className="escolhe-bem-footer">
        <div className="escolhe-bem-footer-logos">
          <img
            src={resolvePublicAssetPath("/images/continente-footer-2.png")}
            alt="Logo Continente"
            className="escolhe-bem-bottom-logo escolhe-bem-bottom-logo-primary"
          />
          <img
            src={resolvePublicAssetPath("/images/volta.png")}
            alt=""
            className="escolhe-bem-bottom-logo escolhe-bem-bottom-logo-secondary"
          />
        </div>
        <PointsFeedback
          key={pointsFlashKey}
          lastPoints={lastPoints}
          isRunning={isRunning}
        />
      </footer>

      {isManagementOpen ? (
        <div className="escolhe-bem-admin-overlay">
          <div className="escolhe-bem-admin-panel">
            <h2>Gestão do Joga e Ganha</h2>

            <div className="escolhe-bem-admin-settings">
              <label>
                Tempo (segundos)
                <input
                  type="text"
                  inputMode="numeric"
                  value={adminInputDrafts.durationSeconds}
                  onChange={(event) =>
                    setAdminInputDrafts((currentDrafts) => ({
                      ...currentDrafts,
                      durationSeconds: event.target.value,
                    }))
                  }
                  onBlur={() => commitNumericDraft("durationSeconds")}
                  onKeyDown={(event) => {
                    if (event.key !== "Enter") return;
                    commitNumericDraft("durationSeconds");
                    (event.currentTarget as HTMLInputElement).blur();
                  }}
                />
              </label>

              <label>
                Objetivo (pontos)
                <input
                  type="text"
                  inputMode="numeric"
                  value={adminInputDrafts.targetScore}
                  onChange={(event) =>
                    setAdminInputDrafts((currentDrafts) => ({
                      ...currentDrafts,
                      targetScore: event.target.value,
                    }))
                  }
                  onBlur={() => commitNumericDraft("targetScore")}
                  onKeyDown={(event) => {
                    if (event.key !== "Enter") return;
                    commitNumericDraft("targetScore");
                    (event.currentTarget as HTMLInputElement).blur();
                  }}
                />
              </label>

              <label>
                Produtos por segundo
                <input
                  type="text"
                  inputMode="decimal"
                  value={adminInputDrafts.productsPerSecond}
                  onChange={(event) =>
                    setAdminInputDrafts((currentDrafts) => ({
                      ...currentDrafts,
                      productsPerSecond: event.target.value,
                    }))
                  }
                  onBlur={() => commitNumericDraft("productsPerSecond")}
                  onKeyDown={(event) => {
                    if (event.key !== "Enter") return;
                    commitNumericDraft("productsPerSecond");
                    (event.currentTarget as HTMLInputElement).blur();
                  }}
                />
              </label>

              <label>
                Últimos segundos mais rápidos
                <input
                  type="text"
                  inputMode="numeric"
                  value={adminInputDrafts.finalRushSeconds}
                  onChange={(event) =>
                    setAdminInputDrafts((currentDrafts) => ({
                      ...currentDrafts,
                      finalRushSeconds: event.target.value,
                    }))
                  }
                  onBlur={() => commitNumericDraft("finalRushSeconds")}
                  onKeyDown={(event) => {
                    if (event.key !== "Enter") return;
                    commitNumericDraft("finalRushSeconds");
                    (event.currentTarget as HTMLInputElement).blur();
                  }}
                />
              </label>

              <label>
                Multiplicador de velocidade final
                <input
                  type="text"
                  inputMode="decimal"
                  value={adminInputDrafts.finalRushSpeedMultiplier}
                  onChange={(event) =>
                    setAdminInputDrafts((currentDrafts) => ({
                      ...currentDrafts,
                      finalRushSpeedMultiplier: event.target.value,
                    }))
                  }
                  onBlur={() => commitNumericDraft("finalRushSpeedMultiplier")}
                  onKeyDown={(event) => {
                    if (event.key !== "Enter") return;
                    commitNumericDraft("finalRushSpeedMultiplier");
                    (event.currentTarget as HTMLInputElement).blur();
                  }}
                />
              </label>
            </div>

            <div className="escolhe-bem-admin-groups">
              <section className="escolhe-bem-admin-group">
                <div className="escolhe-bem-admin-group-header">
                  <h3>Produtos de prémio (vitória)</h3>
                  <button type="button" onClick={resetGivenCounts}>
                    Repor contagem entregues
                  </button>
                </div>

                <p>
                  Produtos de prémio ativos:{" "}
                  {
                    getEligibleVictoryProducts(gameConfig.victoryProductStock)
                      .length
                  }
                  . O sorteio usa peso igual ao stock atual por produto; quando
                  o stock de um produto chega a 0, deixa de sair.
                </p>

                <div className="escolhe-bem-admin-products-grid">
                  {ALL_VICTORY_PRODUCT_IMAGES.map((productPath) => {
                    const stockValue =
                      gameConfig.victoryProductStock[productPath] ?? 0;
                    const isActive = stockValue > 0;
                    const givenValue = givenCounts[productPath] ?? 0;

                    return (
                      <div
                        key={`victory-${productPath}`}
                        className={`product-tile prize-tile ${
                          isActive ? "selected" : ""
                        }`}>
                        <div className="product-tile-display">
                          <img
                            src={resolvePublicAssetPath(
                              toOptimizedEscolheBemProductPath(productPath),
                            )}
                            onError={(event) =>
                              handleProductImageLoadError(
                                event,
                                toOriginalEscolheBemProductPath(productPath),
                              )
                            }
                            alt={formatProductLabel(productPath)}
                          />
                          <span>{formatProductLabel(productPath)}</span>
                        </div>
                        <label className="product-tile-stock">
                          Stock
                          <input
                            type="text"
                            inputMode="numeric"
                            value={stockDrafts[productPath] ?? ""}
                            onChange={(event) =>
                              setStockDrafts((previousDrafts) => ({
                                ...previousDrafts,
                                [productPath]: event.target.value,
                              }))
                            }
                            onFocus={() => {
                              editingStockPathRef.current = productPath;
                            }}
                            onBlur={() => {
                              editingStockPathRef.current = null;
                              commitStockDraft(productPath);
                            }}
                            onKeyDown={(event) => {
                              if (event.key !== "Enter") return;
                              commitStockDraft(productPath);
                              (
                                event.currentTarget as HTMLInputElement
                              ).blur();
                            }}
                          />
                        </label>
                        <div className="product-tile-given">
                          Entregues: {givenValue}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>

              <p>
                Pode configurar secções com pontos positivos e negativos e
                atribuir os produtos a cada secção.
              </p>
              {gameConfig.pointGroups.map((group, index) => {
                const sectionProducts =
                  index === 0
                    ? POSITIVE_PRODUCT_IMAGES
                    : index === 1
                      ? NEGATIVE_PRODUCT_IMAGES
                      : ALL_PRODUCT_IMAGES;
                const sectionTitle = `Secção ${index + 1}`;

                return (
                  <section key={group.id} className="escolhe-bem-admin-group">
                    <div className="escolhe-bem-admin-group-header">
                      <h3>{sectionTitle}</h3>
                      <label>
                        Pontos
                        <input
                          type="number"
                          min={-100}
                          max={100}
                          value={group.points}
                          onChange={(event) =>
                            updateGroupPoints(
                              group.id,
                              Number(event.target.value),
                            )
                          }
                        />
                      </label>
                      <button
                        type="button"
                        onClick={() => removePointGroup(group.id)}
                        disabled={gameConfig.pointGroups.length <= 1}>
                        Remover secção
                      </button>
                    </div>

                    <p>Produtos desta secção: {group.products.length}</p>

                    <div className="escolhe-bem-admin-products-grid">
                      {sectionProducts.map((productPath) => {
                        const isSelected = group.products.includes(productPath);

                        return (
                          <button
                            type="button"
                            key={`${group.id}-${productPath}`}
                            className={`product-tile ${
                              isSelected ? "selected" : ""
                            }`}
                            onClick={() =>
                              toggleProductInGroup(group.id, productPath)
                            }>
                            <img
                              src={resolvePublicAssetPath(
                                toOptimizedEscolheBemProductPath(productPath),
                              )}
                              onError={(event) =>
                                handleProductImageLoadError(
                                  event,
                                  toOriginalEscolheBemProductPath(productPath),
                                )
                              }
                              alt={formatProductLabel(productPath)}
                            />
                            <span>{formatProductLabel(productPath)}</span>
                          </button>
                        );
                      })}
                    </div>
                  </section>
                );
              })}
            </div>

            <div className="escolhe-bem-admin-actions">
              <button type="button" onClick={addPointGroup}>
                Adicionar secção
              </button>
              <button type="button" onClick={resetToDefaultConfig}>
                Repor predefinições
              </button>
              <button type="button" onClick={() => setIsManagementOpen(false)}>
                Fechar gestão
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
